import { useState, useEffect, useCallback, FC } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import css from './index.module.css';

import SEO from "@components/seo";
import MainContent from "@components/content";
import useUserData from "@hooks/useUserData";
import ContainerMini from "@components/containerMini";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Popup from "@components/popup";

export default function AdmContainer () {
  let { id } = useParams();
  const [container, setContainer] = useState<any>({});
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    axios.get(`${import.meta.env.VITE_API_ADM_URL}/container/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('authToken')
      }
    })
    .then(response  => {
      setContainer(response.data);
    })
    .catch(error => {
      setContainer({});
    });
  }, []);

  const handleDelete = (e: any) => {
    e.preventDefault();
    setShowModal(true);
  }

  return (
    <MainContent>    
      <SEO 
        title="Контейнер"
        description=""
        ogUrl="https://artcons.io/"
      />

      <DeleteContainer showModal={showModal} setShowModal={setShowModal} />

      <div className={css.head}>
        <h1>Контейнер</h1>
      </div>

      <form>
        <h2>Основные данные</h2>

        <div className={css.data}>
          <a>ID</a>
          <b>{container?.id}</b>
        </div>

        <div className={css.data}>
          <a>userID</a>
          <b>{container?.userId}</b>

          <Link to={`/admin/user/${container?.userId}`} target="_blank">Перейти в карточку</Link>
        </div>

        <div className={css.data}>
          <a>serverId</a>
          <b>{container?.serverId}</b>

          <Link to={`https://timeweb.cloud/my/servers/${container.serverId}`} target="_blank">Перейти в Timeweb.Cloud</Link>
        </div>

        <div className={css.data}>
          <a>Название</a>
          <b>{container?.name}</b>
        </div>

        <div className={css.data}>
          <a>GTM Token</a>
          <b>{container?.gtmToken}</b>
        </div>

        <div className={css.data}>
          <a>{container?.ips?.find((i: any) => i.type === 'ipv4') ? "IPv4" : "IPv6"}</a>
          <b>{container?.ips?.find((i: any) => i.type === 'ipv4')?.ip}</b>
        </div>

        <div className={css.data}>
          <a>{container?.ips?.find((i: any) => i.type === 'ipv6') ? "IPv6" : "IPv4"}</a>
          <b>{container?.ips?.find((i: any) => i.type === 'ipv6')?.ip}</b>
        </div>

        <div className={css.data}>
          <a>Основной домен</a>
          <b>{container?.domains?.find((i: any) => i.type === 0)?.value}</b>
          <a href={`https://${container?.domains?.find((i: any) => i.type === 0)?.value}/healthy`} target="_blank">Проверить доступность</a>
        </div>

        <div className={css.data}>
          <a>Домен для Preview-сервера</a>
          <b>{container?.domains?.find((i: any) => i.type === 1)?.value}</b>
          <a href={`https://${container?.domains?.find((i: any) => i.type === 1)?.value}/healthy`} target="_blank">Проверить доступность</a>
        </div>

        <div className={css.data}>
          <a>Тариф</a>
          {container?.tariff === 0 && <b>Базовый — 2 500 ₽/мес</b>}
          {container?.tariff === 1 && <b>Стандартный — 5 000 ₽/мес</b>}
          {container?.tariff === 2 && <b>Бизнес — 10 000 ₽/мес</b>}
        </div>

        <div className={css.data}>
          <a>Локация</a>
          {container?.location === 'ru-0' && <b>Россия (Санкт-Петербург)</b>}
          {container?.location === 'ru-1' && <b>Россия (Москва)</b>}
          {container?.location === 'nl-0' && <b>Нидерланды (Амстердам)</b>}
        </div>

        <div className={css.data}>
          <a>Статус</a>
          <b>{container?.status === 5 ? 'Активен' : 'Настраивается'}</b>
        </div>

        <div className={css.data}>
          <a>Дата создания</a>
          <b>{new Date(container?.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</b>
        </div>
        
        <div className={css.data}>
          <a>Стоимость </a>
          <b>
            {(() => {
              let base = container?.tariff === 0 ? 2500 : container?.tariff === 1 ? 5000 : 10000;
              if (container?.location === 'nl-0') base += 300;
              return `${base.toLocaleString('ru-RU')} ₽`;
            })()}
          </b>
        </div>

        <div className={css.data}>
          <a>Комментарий</a>
          <b>{container?.adminComment || 'Нет комментария'}</b>
        </div>
        
        <button className={css.delete} onClick={handleDelete}>Удалить контейнер</button>
      </form>
    </MainContent>
  )
}

interface Modal {
  showModal: boolean; 
  setShowModal: any;
}

export const DeleteContainer:FC<Modal> = ({ showModal, setShowModal }) => {
  let { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleSubmit = async (event: any) => {
    event.preventDefault();
    axios.delete(`${import.meta.env.VITE_API_ADM_URL}/container/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('authToken')
      }
    }).then(_ => {       
      toast.success(t('gtm.deleted'));
      navigate(`/admin/containers`);
    }).catch(_ => {
      toast.error(t('gtm.err'));
    });
  };

  return (
    <Popup showPopup={showModal} setShowPopup={setShowModal}>
      <h5 style={{ fontSize: '17px', marginBottom: 10, marginTop: 0 }}>{t('gtm.deleteHead')}</h5>
      <p style={{color: "var(--text-color)"}}>{t('gtm.deleteP')}</p>

      <form onSubmit={handleSubmit}>
        <button style={{ background: "#e52e2e", width: "100%" }} className="btn" type="submit">{t('gtm.deteleBtn')}</button>
      </form>
    </Popup>
  );
}